/**
 * Registration form UX enhancements:
 *   1. Hungarian tax ID input mask + checksum validation (XXXXXXXX-X-XX)
 *   2. NAV company lookup proxy call when tax ID is valid (auto-fills company
 *      name + HQ address). Backend endpoint at /apps/b2b-register/nav-lookup.
 *   3. Phone input mask with HU default prefix.
 *   4. ZIP → city autofill (basic HU mapping) + sales-person banner.
 *
 * Vanilla JS, no deps. Idempotent — safe to load multiple times on the page.
 */

(function () {
  if (window.__egRegFormUxLoaded) return;
  window.__egRegFormUxLoaded = true;

  // ---------- 1. TAX ID ---------- //

  /** Hungarian tax ID checksum: weights 9-7-3-1-9-7-3 on the first 7 digits.
   * Sum mod 10 → if 0, control digit is 0; else 10 - (sum mod 10).
   * The 8th digit must equal the control digit.
   */
  function isTaxIdValid(rawDigits) {
    if (!/^\d{8,11}$/.test(rawDigits)) return false;
    const weights = [9, 7, 3, 1, 9, 7, 3];
    let sum = 0;
    for (let i = 0; i < 7; i++) sum += parseInt(rawDigits[i], 10) * weights[i];
    const mod = sum % 10;
    const control = mod === 0 ? 0 : 10 - mod;
    return parseInt(rawDigits[7], 10) === control;
  }

  function formatTaxId(digits) {
    // 12345678-1-23  (8-1-2)
    const d = digits.slice(0, 11);
    let out = d.slice(0, 8);
    if (d.length > 8) out += '-' + d.slice(8, 9);
    if (d.length > 9) out += '-' + d.slice(9, 11);
    return out;
  }

  function attachTaxIdMask(input) {
    if (!input) return;
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('maxlength', '13');
    input.setAttribute('autocomplete', 'off');

    let lastValid8 = '';

    function handleInput() {
      const digits = input.value.replace(/\D/g, '').slice(0, 11);
      input.value = formatTaxId(digits);
      input.setCustomValidity('');

      if (digits.length >= 8) {
        if (!isTaxIdValid(digits)) {
          input.setCustomValidity('Érvénytelen adószám (ellenőrző számjegy nem stimmel).');
          showFieldError(input, 'Érvénytelen adószám — kérlek ellenőrizd a számokat.');
        } else {
          clearFieldError(input);
          // Trigger NAV lookup once per fresh 8-digit prefix
          if (digits.slice(0, 8) !== lastValid8) {
            lastValid8 = digits.slice(0, 8);
            triggerNavLookup(digits.slice(0, 8));
          }
        }
      } else {
        clearFieldError(input);
      }
    }

    input.addEventListener('input', handleInput);
    input.addEventListener('blur', handleInput);
  }

  // ---------- 2. NAV LOOKUP (proxy) ---------- //

  function triggerNavLookup(taxId8) {
    const company = document.getElementById('reg-company');
    const billingZip = document.getElementById('reg-billing-zip');
    const billingCity = document.getElementById('reg-billing-city');
    const billingAddress = document.getElementById('reg-billing-address');

    // Only auto-fill empty fields — never overwrite user-entered data.
    const allEmpty = [company, billingZip, billingCity, billingAddress].every(
      (el) => el && el.value.trim() === ''
    );
    if (!allEmpty) return;

    fetch(`/apps/b2b-register/nav-lookup?tax_id=${encodeURIComponent(taxId8)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.found) return;
        if (company && !company.value && data.name) company.value = data.name;
        if (billingZip && !billingZip.value && data.zip) {
          billingZip.value = data.zip;
          billingZip.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (billingCity && !billingCity.value && data.city) billingCity.value = data.city;
        if (billingAddress && !billingAddress.value && data.address) billingAddress.value = data.address;
        // Highlight the auto-filled fields briefly
        [company, billingZip, billingCity, billingAddress].forEach((el) => {
          if (el && el.value) {
            el.classList.add('eg-reg-autofilled');
            setTimeout(() => el.classList.remove('eg-reg-autofilled'), 2000);
          }
        });
      })
      .catch(() => {});
  }

  // ---------- 3. PHONE MASK ---------- //

  function formatHuPhone(digits) {
    // +36 XX XXX XXXX  (no country variant for now — defaults to HU)
    const d = digits.slice(0, 9);
    if (d.length === 0) return '';
    let out = '+36';
    if (d.length > 0) out += ' ' + d.slice(0, 2);
    if (d.length > 2) out += ' ' + d.slice(2, 5);
    if (d.length > 5) out += ' ' + d.slice(5, 9);
    return out;
  }

  function attachPhoneMask(input) {
    if (!input) return;
    input.setAttribute('inputmode', 'tel');
    input.setAttribute('autocomplete', 'tel');
    input.setAttribute('maxlength', '17');

    function handleInput() {
      let raw = input.value;
      // Strip everything except digits, then drop leading "36" or "06" so the
      // user can type either way and we end up with the local 9 digits.
      let digits = raw.replace(/\D/g, '');
      if (digits.startsWith('36')) digits = digits.slice(2);
      else if (digits.startsWith('06')) digits = digits.slice(2);
      digits = digits.slice(0, 9);
      input.value = formatHuPhone(digits);
      if (digits.length > 0 && digits.length < 9) {
        input.setCustomValidity('A telefonszám 9 számjegy hosszú a +36 után.');
      } else {
        input.setCustomValidity('');
      }
    }

    input.addEventListener('input', handleInput);
    input.addEventListener('blur', handleInput);
  }

  // ---------- 5. EMAIL TYPO SUGGESTION ---------- //

  const EMAIL_DOMAINS = [
    'gmail.com', 'googlemail.com',
    'yahoo.com', 'yahoo.co.uk', 'yahoo.hu',
    'hotmail.com', 'hotmail.co.uk', 'hotmail.hu',
    'outlook.com', 'outlook.hu', 'live.com', 'live.hu',
    'icloud.com', 'me.com', 'mac.com',
    'aol.com', 'protonmail.com', 'proton.me',
    'freemail.hu', 'citromail.hu', 't-online.hu', 'vipmail.hu',
    'index.hu', 'invitel.hu', 'chello.hu',
  ];
  const EMAIL_TLDS = ['com', 'hu', 'net', 'org', 'co.uk', 'de', 'eu', 'io'];

  function levenshtein(a, b) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    const prev = Array(b.length + 1).fill(0).map((_, i) => i);
    const curr = Array(b.length + 1).fill(0);
    for (let i = 1; i <= a.length; i++) {
      curr[0] = i;
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      }
      for (let k = 0; k <= b.length; k++) prev[k] = curr[k];
    }
    return prev[b.length];
  }

  function suggestDomain(domain) {
    if (!domain) return null;
    const d = domain.toLowerCase();
    if (EMAIL_DOMAINS.includes(d)) return null;
    let best = null;
    let bestDist = 99;
    for (const known of EMAIL_DOMAINS) {
      const dist = levenshtein(d, known);
      if (dist < bestDist && dist > 0 && dist <= 2) {
        bestDist = dist;
        best = known;
      }
    }
    return best;
  }

  function attachEmailSuggestion(input) {
    if (!input) return;
    function check() {
      // Don't disturb readonly (logged-in) email field
      if (input.readOnly) return;
      const value = input.value.trim();
      const at = value.lastIndexOf('@');
      if (at < 1 || at === value.length - 1) {
        clearEmailSuggestion(input);
        return;
      }
      const local = value.slice(0, at);
      const domain = value.slice(at + 1);
      const suggested = suggestDomain(domain);
      if (suggested) {
        showEmailSuggestion(input, `${local}@${suggested}`);
      } else {
        clearEmailSuggestion(input);
      }
    }
    input.addEventListener('blur', check);
    input.addEventListener('input', () => {
      // Clear suggestion as user types again
      clearEmailSuggestion(input);
    });
  }

  function showEmailSuggestion(input, suggested) {
    let el = input.parentElement.querySelector('.eg-reg-email-suggest');
    if (!el) {
      el = document.createElement('small');
      el.className = 'eg-reg-email-suggest';
      input.parentElement.appendChild(el);
    }
    el.innerHTML = `Talán ezt akartad írni: <button type="button" class="eg-reg-email-suggest-link"></button>?`;
    const btn = el.querySelector('.eg-reg-email-suggest-link');
    btn.textContent = suggested;
    btn.addEventListener('click', () => {
      input.value = suggested;
      clearEmailSuggestion(input);
      input.focus();
    });
  }
  function clearEmailSuggestion(input) {
    const el = input.parentElement.querySelector('.eg-reg-email-suggest');
    if (el) el.remove();
  }

  // ---------- 4. ZIP HELPERS ---------- //

  // Tiny ZIP → city table for the most common HU postal codes. Covers Budapest
  // districts (1xxx) and ~50 large cities. Unknown ZIPs leave the city field
  // empty; the user can type it manually.
  const ZIP_TO_CITY = {
    // Budapest districts handled separately by prefix logic below
    '2030': 'Érd', '2040': 'Budaörs', '2089': 'Telki', '2092': 'Budakeszi',
    '2100': 'Gödöllő', '2120': 'Dunakeszi', '2151': 'Fót', '2170': 'Aszód',
    '2200': 'Monor', '2310': 'Szigetszentmiklós', '2400': 'Dunaújváros',
    '2500': 'Esztergom', '2600': 'Vác', '2700': 'Cegléd', '2800': 'Tatabánya',
    '2900': 'Komárom', '3000': 'Hatvan', '3100': 'Salgótarján', '3200': 'Gyöngyös',
    '3300': 'Eger', '3400': 'Mezőkövesd', '3500': 'Miskolc', '3700': 'Kazincbarcika',
    '3800': 'Szikszó', '3900': 'Szerencs', '4000': 'Debrecen', '4220': 'Hajdúböszörmény',
    '4400': 'Nyíregyháza', '4600': 'Kisvárda', '4700': 'Mátészalka', '4800': 'Vásárosnamény',
    '5000': 'Szolnok', '5100': 'Jászberény', '5200': 'Törökszentmiklós',
    '5300': 'Karcag', '5400': 'Mezőtúr', '5500': 'Békéscsaba', '5600': 'Békéscsaba',
    '5700': 'Gyula', '5900': 'Orosháza', '6000': 'Kecskemét', '6100': 'Kiskunfélegyháza',
    '6200': 'Kiskőrös', '6300': 'Kalocsa', '6400': 'Kiskunhalas', '6500': 'Baja',
    '6600': 'Szentes', '6720': 'Szeged', '6800': 'Hódmezővásárhely', '6900': 'Makó',
    '7000': 'Sárbogárd', '7100': 'Szekszárd', '7200': 'Dombóvár', '7300': 'Komló',
    '7400': 'Kaposvár', '7500': 'Nagyatád', '7570': 'Barcs', '7600': 'Pécs',
    '7700': 'Mohács', '7800': 'Siklós', '7900': 'Szigetvár', '8000': 'Székesfehérvár',
    '8100': 'Várpalota', '8200': 'Veszprém', '8300': 'Tapolca', '8400': 'Ajka',
    '8500': 'Pápa', '8600': 'Siófok', '8700': 'Marcali', '8800': 'Nagykanizsa',
    '8900': 'Zalaegerszeg', '9000': 'Győr', '9100': 'Tét', '9200': 'Mosonmagyaróvár',
    '9300': 'Csorna', '9400': 'Sopron', '9500': 'Celldömölk', '9600': 'Sárvár',
    '9700': 'Szombathely', '9800': 'Vasvár', '9900': 'Körmend',
  };

  function lookupCity(zip) {
    if (!/^\d{4}$/.test(zip)) return null;
    if (zip.startsWith('1')) return 'Budapest'; // all 1000-1999
    return ZIP_TO_CITY[zip] || null;
  }

  // Mini ZIP → county code map (matches the app's zipToCounty.ts ranges).
  const ZIP_RANGES = [
    [1000, 1999, 'HU-BU', 'Budapest'],
    [2000, 2499, 'HU-PE', 'Pest'],
    [2500, 2519, 'HU-KE', 'Komárom-Esztergom'],
    [2520, 2799, 'HU-PE', 'Pest'],
    [2800, 2999, 'HU-KE', 'Komárom-Esztergom'],
    [3000, 3099, 'HU-HE', 'Heves'],
    [3100, 3199, 'HU-NO', 'Nógrád'],
    [3200, 3399, 'HU-HE', 'Heves'],
    [3400, 3499, 'HU-NO', 'Nógrád'],
    [3500, 3999, 'HU-BZ', 'Borsod-Abaúj-Zemplén'],
    [4000, 4299, 'HU-HB', 'Hajdú-Bihar'],
    [4300, 4999, 'HU-SZ', 'Szabolcs-Szatmár-Bereg'],
    [5000, 5499, 'HU-JN', 'Jász-Nagykun-Szolnok'],
    [5500, 5999, 'HU-BE', 'Békés'],
    [6000, 6599, 'HU-BK', 'Bács-Kiskun'],
    [6600, 6999, 'HU-CS', 'Csongrád-Csanád'],
    [7000, 7299, 'HU-TO', 'Tolna'],
    [7300, 7399, 'HU-BA', 'Baranya'],
    [7400, 7599, 'HU-SO', 'Somogy'],
    [7600, 7999, 'HU-BA', 'Baranya'],
    [8000, 8199, 'HU-FE', 'Fejér'],
    [8200, 8599, 'HU-VE', 'Veszprém'],
    [8600, 8799, 'HU-SO', 'Somogy'],
    [8800, 8999, 'HU-ZA', 'Zala'],
    [9000, 9499, 'HU-GS', 'Győr-Moson-Sopron'],
    [9500, 9999, 'HU-VA', 'Vas'],
  ];

  function zipToCounty(zipStr) {
    if (!/^\d{4}$/.test(zipStr)) return null;
    const n = parseInt(zipStr, 10);
    for (const [a, b, code, name] of ZIP_RANGES) {
      if (n >= a && n <= b) return { code, name };
    }
    return null;
  }

  function attachZipHelpers(zipInput, cityInput) {
    if (!zipInput) return;
    zipInput.setAttribute('inputmode', 'numeric');
    zipInput.setAttribute('maxlength', '4');
    zipInput.setAttribute('pattern', '\\d{4}');

    zipInput.addEventListener('input', () => {
      const v = zipInput.value.replace(/\D/g, '').slice(0, 4);
      if (v !== zipInput.value) zipInput.value = v;
      // Auto-fill city when ZIP is a known one and city is empty
      if (v.length === 4 && cityInput && cityInput.value.trim() === '') {
        const city = lookupCity(v);
        if (city) cityInput.value = city;
      }
      // Sales person banner only fires for the billing ZIP
      if (zipInput.id === 'reg-billing-zip') updateSalesPersonBanner(v);
    });
  }

  // ---------- SALES PERSON BANNER ---------- //

  let salesTeamCache = null;
  function fetchSalesTeam() {
    if (salesTeamCache !== null) return Promise.resolve(salesTeamCache);
    // The shop metafield is already publicly readable on the storefront.
    // The page-register.liquid template inlines it as window.__egSalesTeam.
    if (Array.isArray(window.__egSalesTeam)) {
      salesTeamCache = window.__egSalesTeam;
    } else {
      salesTeamCache = [];
    }
    return Promise.resolve(salesTeamCache);
  }

  function findSalesPersonForCounty(countyCode, team) {
    if (!countyCode || !Array.isArray(team)) return null;
    const codes = [countyCode];
    if (countyCode === 'HU-BU') codes.push('HU-PE');
    if (countyCode === 'HU-PE') codes.push('HU-BU');
    return team.find((sp) => Array.isArray(sp.counties) && codes.some((c) => sp.counties.includes(c))) || null;
  }

  function updateSalesPersonBanner(zip) {
    const banner = document.getElementById('eg-reg-sales-banner');
    if (!banner) return;
    if (!/^\d{4}$/.test(zip)) {
      banner.hidden = true;
      return;
    }
    const county = zipToCounty(zip);
    if (!county) {
      banner.hidden = true;
      return;
    }
    fetchSalesTeam().then((team) => {
      const sp = findSalesPersonForCounty(county.code, team);
      if (!sp) {
        banner.hidden = true;
        return;
      }
      banner.querySelector('[data-sp-name]').textContent = sp.name || '';
      banner.querySelector('[data-sp-region]').textContent = sp.region || county.name;
      banner.hidden = false;
    });
  }

  // ---------- INLINE FIELD ERROR HELPER ---------- //

  function showFieldError(input, msg) {
    let el = input.parentElement.querySelector('.eg-reg-field-error');
    if (!el) {
      el = document.createElement('small');
      el.className = 'eg-reg-field-error';
      input.parentElement.appendChild(el);
    }
    el.textContent = msg;
    input.classList.add('eg-reg-field-invalid');
  }
  function clearFieldError(input) {
    const el = input.parentElement.querySelector('.eg-reg-field-error');
    if (el) el.remove();
    input.classList.remove('eg-reg-field-invalid');
  }

  // ---------- INIT ---------- //

  function init() {
    attachTaxIdMask(document.getElementById('reg-taxid'));
    attachPhoneMask(document.getElementById('reg-phone'));
    attachEmailSuggestion(document.getElementById('reg-email'));
    attachZipHelpers(
      document.getElementById('reg-billing-zip'),
      document.getElementById('reg-billing-city'),
    );
    attachZipHelpers(
      document.getElementById('reg-shipping-zip'),
      document.getElementById('reg-shipping-city'),
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
